import { getApiPrefix } from '../utils/apiHelper';
import React, { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { DateInput } from "../components/ui/date-input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { Upload, Trash2, Loader2, ArrowUpCircle, ArrowDownCircle, CreditCard, Plus, Layers, ChevronLeft, ChevronRight, Search, Pencil, X, Wallet } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { SortableHeader, sortData } from "../components/ui/sortable-header";
import { ExcelUploadHelper, EXCEL_COLUMNS } from "../components/ui/excel-upload-helper";
import { useTranslation } from "../contexts/I18nContext";

const ITEMS_PER_PAGE = 50;

export default function CardMovementsPage() {
  const { t } = useTranslation();
  const [movements, setMovements] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Pagination & Sort (client-side)
  const [currentPage, setCurrentPage] = useState(1);
  const [sort, setSort] = useState({ field: "data", direction: "desc" });
  
  // Filters (client-side)
  const [filterStato, setFilterStato] = useState("");
  const [filterIntestatario, setFilterIntestatario] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchInput, setSearchInput] = useState("");
  
  const [uploading, setUploading] = useState(false);
  const [manualDialog, setManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({ data_operazione: "", data_valuta: "", ora_operazione: "", descrizione: "", importo: "", intestatario: "" });
  const [savingManual, setSavingManual] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);
  
  const [allocDialog, setAllocDialog] = useState({ open: false, movement: null, allocations: [] });
  const [allocType, setAllocType] = useState("fattura");
  const [allocForm, setAllocForm] = useState({ invoice_id: "", account_id: "", cost_center_id: "", importo: "", note: "", tipo_allocazione: "standard" });
  const [editingAllocationId, setEditingAllocationId] = useState(null);

  const [stats, setStats] = useState({ daAllocare: 0 });
  const [intestatari, setIntestatari] = useState([]);
  const [carte, setCarte] = useState([]);
  
  // Selezione multipla
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const [deleteMultipleDialog, setDeleteMultipleDialog] = useState(false);
  const [deleteAllDialog, setDeleteAllDialog] = useState(false);

  const [allocAuxLoaded, setAllocAuxLoaded] = useState(false);

  const fetchMovements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`${getApiPrefix()}/card-movements?page_size=5000`);
      const items = res.data.items || [];
      setMovements(items);
      
      // Extract unique intestatari from all data
      const uniqueIntestatari = [...new Set(items.map(m => m.intestatario).filter(Boolean))];
      setIntestatari(uniqueIntestatari.sort());
    } catch (err) {
      handleApiError(err, t("errors.loadCardMovements"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAuxData = useCallback(async () => {
    try {
      const [accRes, ccRes, carteRes] = await Promise.all([
        api.get(`${getApiPrefix()}/accounts`),
        api.get(`${getApiPrefix()}/cost-centers`),
        api.get(`${getApiPrefix()}/carte`).catch(() => ({ data: [] })),
      ]);
      setAccounts(accRes.data);
      setCostCenters(ccRes.data);
      setCarte(carteRes.data || []);
    } catch (err) {
      
    }
  }, []);

  const fetchAllocAuxData = useCallback(async () => {
    if (allocAuxLoaded) return;
    try {
      // Endpoint /invoices/allocabili è ottimizzato:
      // - esclude fatture 'pagata' e 'allocata'
      // - include importo_pagato per calcolo residuo lato client
      const invRes = await api.get(`${getApiPrefix()}/invoices/allocabili`);
      setInvoices(invRes.data || []);
      setAllocAuxLoaded(true);
    } catch (err) {
      
    }
  }, [allocAuxLoaded]);

  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await api.get(`${getApiPrefix()}/card-movements/stats`).catch(() => null);
      if (statsRes?.data) {
        setStats(prev => ({ ...prev, daAllocare: statsRes.data.da_allocare || 0 }));
      }
    } catch (err) {
      
    }
  }, []);

  // Calcola stats YTD (da inizio anno) dai movimenti caricati
  const currentYear = new Date().getFullYear();
  const ytdStats = React.useMemo(() => {
    let entrate = 0, uscite = 0;
    movements.forEach(m => {
      let year = null;
      if (m.data) {
        const parts = m.data.split("-");
        if (parts[0].length === 4) year = parseInt(parts[0]);
        else if (parts[2] && parts[2].length === 4) year = parseInt(parts[2]);
      }
      if (year === currentYear) {
        if (m.importo > 0) entrate += m.importo;
        else uscite += Math.abs(m.importo);
      }
    });
    return { entrate, uscite };
  }, [movements, currentYear]);

  // Calcola disponibilità liquide per carta
  const liquiditaCarte = React.useMemo(() => {
    const carteMap = {};
    carte.forEach(c => {
      carteMap[c.id] = { nome: c.nome, numero_carta: c.numero_carta, saldo_iniziale: c.saldo_iniziale || 0, movimenti: 0 };
    });
    movements.forEach(m => {
      if (m.carta_id && carteMap[m.carta_id]) {
        carteMap[m.carta_id].movimenti += (m.importo || 0);
      }
    });
    const list = Object.values(carteMap).map(c => ({
      nome: c.nome,
      numero_carta: c.numero_carta,
      saldo_iniziale: c.saldo_iniziale,
      movimenti: c.movimenti,
      saldo_attuale: c.saldo_iniziale + c.movimenti
    }));
    const totale = list.reduce((sum, c) => sum + c.saldo_attuale, 0);
    return { carte: list, totale };
  }, [movements, carte]);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);
  useEffect(() => { fetchAuxData(); fetchStats(); }, [fetchAuxData, fetchStats]);
  useEffect(() => { setCurrentPage(1); }, [filterStato, filterIntestatario, searchText]);

  // Client-side filtering
  const filteredMovements = movements.filter(m => {
    const matchStato = !filterStato || filterStato === "all" || m.stato_allocazione === filterStato;
    const matchIntestatario = !filterIntestatario || filterIntestatario === "all" || m.intestatario === filterIntestatario;
    const matchSearch = !searchText || (m.descrizione || "").toLowerCase().includes(searchText.toLowerCase());
    return matchStato && matchIntestatario && matchSearch;
  });

  // Client-side sort and paginate
  const sortedMovements = sortData(filteredMovements, sort);
  const totalPages = Math.ceil(sortedMovements.length / ITEMS_PER_PAGE);
  const paginatedMovements = sortedMovements.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleSearch = () => {
    setSearchText(searchInput);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post(`${getApiPrefix()}/card-movements/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      showSuccess(res.data.message);
      fetchMovements();
      fetchStats();
    } catch (err) {
      if (err.code === "ECONNABORTED" || err.message?.includes("timeout")) {
        handleApiError({ message: "Il caricamento sta impiegando troppo tempo. Attendi qualche istante e ricarica la pagina per verificare se i dati sono stati importati. Non riprovare il caricamento per evitare duplicati." });
      } else {
        handleApiError(err, t("errors.errorUpload"));
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleManualCreate = async () => {
    if (!manualForm.data_operazione || !manualForm.descrizione || !manualForm.importo) {
      handleApiError({ message: t("errors.requiredFields") });
      return;
    }
    setSavingManual(true);
    try {
      const params = new URLSearchParams({
        data_operazione: manualForm.data_operazione,
        descrizione: manualForm.descrizione,
        importo: manualForm.importo,
        tipo: "uscita"
      });
      if (manualForm.data_valuta) params.append("data_valuta", manualForm.data_valuta);
      await api.post(`${getApiPrefix()}/card-movements?${params.toString()}`);
      showSuccess(t("success.movementCreated"));
      setManualDialog(false);
      setManualForm({ data_operazione: "", data_valuta: "", ora_operazione: "", descrizione: "", importo: "", intestatario: "" });
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorCreation"));
    } finally {
      setSavingManual(false);
    }
  };

  const [deleteSingleDialog, setDeleteSingleDialog] = useState(null);

  const handleDelete = async (id) => {
    try {
      await api.delete(`${getApiPrefix()}/card-movements/${id}`);
      showSuccess(t("success.movementDeleted"));
      setDeleteSingleDialog(null);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedIds.size;
    setDeletingBulk(true);
    try {
      await api.post(`${getApiPrefix()}/card-movements/bulk-delete`, { ids: Array.from(selectedIds) });
      showSuccess(`${count} movimenti eliminati`);
      setSelectedIds(new Set());
      setDeleteMultipleDialog(false);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorMultiDelete"));
    } finally {
      setDeletingBulk(false);
    }
  };

  const handleDeleteAll = async () => {
    setDeletingBulk(true);
    try {
      await api.delete(`${getApiPrefix()}/card-movements`);
      showSuccess(t("success.allCardMovementsDeleted"));
      setSelectedIds(new Set());
      setDeleteAllDialog(false);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorDeleteTotal"));
    } finally {
      setDeletingBulk(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedMovements.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedMovements.map(m => m.id)));
    }
  };

  const openAllocDialog = async (movement) => {
    try {
      const [allocRes] = await Promise.all([
        api.get(`${getApiPrefix()}/card-movements/${movement.id}/allocations`),
        fetchAllocAuxData(),
      ]);
      setAllocDialog({ open: true, movement, allocations: allocRes.data });
      const totaleAllocato = allocRes.data.reduce((sum, a) => sum + (a.importo || 0), 0);
      const residuo = Math.abs(movement.importo) - totaleAllocato;
      setAllocForm({ ...allocForm, importo: String(Math.max(0, residuo).toFixed(2)) });
      setEditingAllocationId(null);
    } catch (err) {
      handleApiError(err, t("errors.loadAllocations"));
    }
  };

  // Calcola residuo da allocare
  const calcolaResiduo = () => {
    if (!allocDialog.movement) return 0;
    const totaleAllocato = allocDialog.allocations
      .filter(a => a.id !== editingAllocationId)
      .reduce((sum, a) => sum + (a.importo || 0), 0);
    return Math.abs(allocDialog.movement.importo) - totaleAllocato;
  };

  // Residuo aperto su una fattura (totale - già pagato). Usa il campo restituito dal backend.
  const calcolaResiduoFattura = (invoice) => {
    if (!invoice) return 0;
    const totale = Math.abs(parseFloat(invoice.totale) || 0);
    const pagato = Math.abs(parseFloat(invoice.importo_pagato) || 0);
    return Math.max(0, totale - pagato);
  };

  // Quando si seleziona una fattura: pre-popola importo con il MIN tra residuo fattura
  // e residuo del movimento da allocare (mai over-allocazione).
  const handleInvoiceSelect = (invoiceId) => {
    const inv = invoices.find(i => i.id === invoiceId);
    const residuoFattura = calcolaResiduoFattura(inv);
    const residuoMovimento = calcolaResiduo();
    const suggerito = Math.min(
      residuoFattura || Infinity,
      residuoMovimento || Infinity
    );
    const importoFinale = Number.isFinite(suggerito) ? Math.max(0, suggerito) : 0;
    setAllocForm({ ...allocForm, invoice_id: invoiceId, importo: importoFinale ? String(importoFinale.toFixed(2)) : "" });
  };

  // Avvia modifica allocazione
  const startEditAllocation = (alloc) => {
    setEditingAllocationId(alloc.id);
    let tipo = "conto";
    if (alloc.invoice_id) tipo = "fattura";
    else if (alloc.tipo_allocazione === "assicurazioni_veicoli") tipo = "assicurazione";
    else if (alloc.tipo_allocazione === "bollo_veicoli") tipo = "bollo_auto";
    else if (alloc.tipo_allocazione === "carburanti") tipo = "carburante";
    setAllocType(tipo);
    setAllocForm({
      invoice_id: alloc.invoice_id || "",
      account_id: alloc.account_id || "",
      cost_center_id: alloc.cost_center_id || "",
      importo: String(alloc.importo || ""),
      note: alloc.note || "",
      tipo_allocazione: alloc.tipo_allocazione || "standard"
    });
  };

  // Annulla modifica
  const cancelEditAllocation = () => {
    setEditingAllocationId(null);
    const residuo = calcolaResiduo();
    setAllocForm({ invoice_id: "", account_id: "", cost_center_id: "", importo: String(residuo.toFixed(2)), note: "", tipo_allocazione: "standard" });
    setAllocType("fattura");
  };

  const handleAddAllocation = async () => {
    const { movement } = allocDialog;
    if (!movement) return;
    
    const payload = {
      importo: parseFloat(allocForm.importo),
      tipo_allocazione: allocForm.tipo_allocazione,
      note: allocForm.note || null,
      ...(allocType === "fattura" && allocForm.invoice_id ? { invoice_id: allocForm.invoice_id } : {}),
      ...((allocType === "conto" || allocType === "assicurazione" || allocType === "bollo_auto") && allocForm.account_id ? { account_id: allocForm.account_id } : {}),
      ...(allocForm.cost_center_id ? { centro_costo_id: allocForm.cost_center_id } : {})
    };
    
    // Imposta tipo_allocazione in base al tipo selezionato
    if (allocType === "assicurazione") {
      payload.tipo_allocazione = "assicurazioni_veicoli";
    } else if (allocType === "bollo_auto") {
      payload.tipo_allocazione = "bollo_veicoli";
    } else if (allocType === "carburante") {
      payload.tipo_allocazione = "carburanti";
    }
    
    try {
      if (editingAllocationId) {
        await api.put(`${getApiPrefix()}/card-movements/${movement.id}/allocations/${editingAllocationId}`, payload);
        showSuccess(t("success.allocationModified"));
      } else {
        await api.post(`${getApiPrefix()}/card-movements/${movement.id}/allocations`, payload);
        showSuccess(t("success.allocationAdded"));
      }
      const res = await api.get(`${getApiPrefix()}/card-movements/${movement.id}/allocations`);
      setAllocDialog({ ...allocDialog, allocations: res.data });
      setAllocForm({ invoice_id: "", account_id: "", cost_center_id: "", importo: "", note: "", tipo_allocazione: "standard" });
      setEditingAllocationId(null);
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.errorAllocation"));
    }
  };

  const handleDeleteAllocation = async (allocId) => {
    const { movement } = allocDialog;
    if (!movement) return;
    try {
      await api.delete(`${getApiPrefix()}/card-movements/${movement.id}/allocations/${allocId}`);
      showSuccess(t("success.allocationDeleted"));
      const res = await api.get(`${getApiPrefix()}/card-movements/${movement.id}/allocations`);
      setAllocDialog({ ...allocDialog, allocations: res.data });
      fetchMovements();
      fetchStats();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const formatCurrency = (val) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val || 0);

  const getStatoBadge = (stato) => {
    const styles = {
      da_allocare: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
      parziale: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
      in_allocazione: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
      allocato: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
    };
    const labels = { da_allocare: "Da Allocare", parziale: "Parziale", in_allocazione: "In Allocazione", allocato: "Allocato" };
    return <Badge className={styles[stato] || ""}>{labels[stato] || stato}</Badge>;
  };

  const formatDate = (d) => {
    if (!d) return "-";
    if (d.includes("-") && d.split("-")[0].length === 4) {
      const [y,m,dd] = d.split("-");
      return `${dd}/${m}/${y}`;
    }
    return d;
  };

  return (
    <div className="space-y-6" data-testid="card-movements-page">
      <div className="flex items-center gap-3">
        <CreditCard className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("nav.cardMovements")}</h1>
          <p className="text-muted-foreground text-sm">Gestione e allocazione movimenti carte prepagate</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-emerald-500 bg-gradient-to-r from-emerald-50/50 to-transparent dark:from-emerald-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Entrate {currentYear}</p>
                <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">{formatCurrency(ytdStats.entrate)}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <ArrowDownCircle className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500 bg-gradient-to-r from-red-50/50 to-transparent dark:from-red-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">Uscite {currentYear}</p>
                <p className="text-2xl font-bold font-mono text-red-600 mt-1">{formatCurrency(ytdStats.uscite)}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <ArrowUpCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500 bg-gradient-to-r from-amber-50/50 to-transparent dark:from-amber-950/20 hover:shadow-md transition-shadow">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">{t('common.toAllocate')}</p>
                <p className="text-2xl font-bold font-mono mt-1">{stats.daAllocare}</p>
              </div>
              <div className="h-12 w-12 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Layers className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disponibilita Carte */}
      {carte.length > 0 && (
        <Card className="border-l-4 border-l-purple-500 hover:shadow-md transition-shadow" data-testid="liquidita-carte-card">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center gap-2 mb-4">
              <Wallet className="h-5 w-5 text-purple-600" />
              <h3 className="font-semibold text-sm uppercase tracking-wider text-purple-600 dark:text-purple-400">Disponibilita Carte</h3>
              <span className="ml-auto text-2xl font-bold font-mono text-purple-700 dark:text-purple-300" data-testid="liquidita-carte-totale">{formatCurrency(liquiditaCarte.totale)}</span>
            </div>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {liquiditaCarte.carte.map((c, idx) => (
                <div key={`carta-${idx}`} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800">
                  <div>
                    <p className="font-medium text-sm">{c.nome}</p>
                    {c.numero_carta && <p className="text-xs text-muted-foreground">•••• {c.numero_carta.slice(-4)}</p>}
                    <p className="text-xs text-muted-foreground">Iniziale: {formatCurrency(c.saldo_iniziale)}</p>
                  </div>
                  <p className={`font-bold font-mono text-sm ${c.saldo_attuale >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid={`saldo-carta-${idx}`}>
                    {formatCurrency(c.saldo_attuale)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters + Toolbar */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={filterStato} onValueChange={setFilterStato}>
              <SelectTrigger className="w-40"><SelectValue placeholder={t("common.allStatuses")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.all')}</SelectItem>
                <SelectItem value="da_allocare">{t('common.toAllocate')}</SelectItem>
                <SelectItem value="parziale">{t('common.partial')}</SelectItem>
                <SelectItem value="allocato">{t('invoices.allocated')}</SelectItem>
              </SelectContent>
            </Select>
            <SearchableSelect
              options={[
                { value: "all", label: "Tutti" },
                ...intestatari.map(i => ({ value: i, label: i }))
              ]}
              value={filterIntestatario}
              onValueChange={setFilterIntestatario}
              placeholder="Tutti intestatari"
              searchPlaceholder="Cerca intestatario..."
              testId="filter-intestatario"
            />
            <div className="flex gap-2">
              <Input 
                placeholder={t("common.searchDescription")} 
                value={searchInput} 
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="w-48"
              />
              <Button variant="outline" size="icon" onClick={handleSearch}><Search className="h-4 w-4" /></Button>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{filteredMovements.length} di {movements.length} movimenti</span>
              <Button variant="outline" onClick={() => setManualDialog(true)}><Plus className="h-4 w-4 mr-2" />{t('common.manual')}</Button>
              <ExcelUploadHelper
                columns={EXCEL_COLUMNS.movimenti_carta}
                onUpload={handleUpload}
                uploading={uploading}
                buttonText="Importa Excel"
                title="Struttura File - Movimenti Carta"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Selection toolbar */}
      {selectedIds.size > 0 && (
        <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
          <CardContent className="py-3">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">{selectedIds.size} moviment{selectedIds.size === 1 ? 'o' : 'i'} selezionat{selectedIds.size === 1 ? 'o' : 'i'}</span>
              <Button variant="destructive" size="sm" onClick={() => setDeleteMultipleDialog(true)} disabled={deletingBulk} data-testid="bulk-delete-card-btn">
                {deletingBulk ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Elimina selezionati
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>{t('common.deselect')}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete All button */}
      {movements.length > 0 && selectedIds.size === 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteAllDialog(true)} disabled={deletingBulk} data-testid="delete-all-card-btn">
            {deletingBulk ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Elimina tutti ({movements.length})
          </Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="border rounded-xl overflow-hidden shadow-sm bg-card">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-900/50 hover:bg-slate-50 border-b-2">
                    <TableHead className="w-[40px]">
                      <Checkbox checked={sortedMovements.length > 0 && selectedIds.size === sortedMovements.length} onCheckedChange={toggleSelectAll} data-testid="select-all-card" />
                    </TableHead>
                    <TableHead className="w-[95px]"><SortableHeader field="data" currentSort={sort} onSort={setSort}>Data Op.</SortableHeader></TableHead>
                    <TableHead className="w-[95px]"><SortableHeader field="data_valuta" currentSort={sort} onSort={setSort}>Data Reg.</SortableHeader></TableHead>
                    <TableHead className="w-[65px]"><SortableHeader field="ora_operazione" currentSort={sort} onSort={setSort}>{t('common.now')}</SortableHeader></TableHead>
                    <TableHead><SortableHeader field="descrizione" currentSort={sort} onSort={setSort}>{t("common.description")}</SortableHeader></TableHead>
                    <TableHead className="w-[70px]"><SortableHeader field="intestatario" currentSort={sort} onSort={setSort}>Intest.</SortableHeader></TableHead>
                    <TableHead className="w-[90px]"><SortableHeader field="carta_numero" currentSort={sort} onSort={setSort}>{t('common.card')}</SortableHeader></TableHead>
                    <TableHead className="w-[100px] text-right"><SortableHeader field="importo" currentSort={sort} onSort={setSort} align="right">{t("common.amount")}</SortableHeader></TableHead>
                    <TableHead className="w-[100px] text-right"><SortableHeader field="importo_allocato" currentSort={sort} onSort={setSort} align="right">{t('invoices.allocated')}</SortableHeader></TableHead>
                    <TableHead className="w-[95px]"><SortableHeader field="stato_allocazione" currentSort={sort} onSort={setSort}>{t("common.status")}</SortableHeader></TableHead>
                    <TableHead className="w-[80px] text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedMovements.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center py-16 text-muted-foreground">Nessun movimento carta trovato</TableCell></TableRow>
                  ) : paginatedMovements.map((row) => (
                    <TableRow key={row.id} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800 ${selectedIds.has(row.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`} data-testid={`row-${row.id}`}>
                      <TableCell><Checkbox checked={selectedIds.has(row.id)} onCheckedChange={() => toggleSelect(row.id)} /></TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(row.data)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(row.data_valuta)}</TableCell>
                      <TableCell className="whitespace-nowrap">{row.ora_operazione || "-"}</TableCell>
                      <TableCell className="max-w-[250px] truncate" title={row.descrizione}>{row.descrizione}</TableCell>
                      <TableCell>{row.intestatario || "-"}</TableCell>
                      <TableCell>{row.carta_numero ? `•••• ${String(row.carta_numero).slice(-4)}` : "-"}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(row.importo)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatCurrency(row.importo_allocato)}</TableCell>
                      <TableCell>{getStatoBadge(row.stato_allocazione)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openAllocDialog(row)} title={t("invoices.allocateMovement")}><Layers className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => setDeleteSingleDialog(row.id)} title={t("common.delete")}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between pb-16">
            <div className="text-sm text-muted-foreground">
              Pagina {currentPage} di {totalPages || 1} ({sortedMovements.length} di {movements.length} movimenti)
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft className="h-4 w-4 mr-1" />{t('common.previous')}</Button>
              <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>{t('common.nextLabel')}<ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialog} onOpenChange={setManualDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuovo Movimento Carta</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Data Operazione *</Label><DateInput value={manualForm.data_operazione} onChange={(e) => setManualForm({ ...manualForm, data_operazione: e.target.value })} /></div>
              <div><Label>Data Registrazione</Label><DateInput value={manualForm.data_valuta} onChange={(e) => setManualForm({ ...manualForm, data_valuta: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t('common.now')}</Label><Input value={manualForm.ora_operazione} onChange={(e) => setManualForm({ ...manualForm, ora_operazione: e.target.value })} placeholder="HH:MM" /></div>
              <div><Label>Intestatario</Label><Input value={manualForm.intestatario} onChange={(e) => setManualForm({ ...manualForm, intestatario: e.target.value })} /></div>
            </div>
            <div><Label>Descrizione *</Label><Input value={manualForm.descrizione} onChange={(e) => setManualForm({ ...manualForm, descrizione: e.target.value })} /></div>
            <div><Label>{t('common.amount')} *</Label><Input type="number" step="0.01" value={manualForm.importo} onChange={(e) => setManualForm({ ...manualForm, importo: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialog(false)} disabled={savingManual}>{t("common.cancel")}</Button>
            <Button onClick={handleManualCreate} disabled={savingManual}>
              {savingManual ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('dialogs.creating')}</> : 'Crea'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Allocation Dialog */}
      <Dialog open={allocDialog.open} onOpenChange={(open) => { 
        setAllocDialog({ ...allocDialog, open }); 
        if (!open) { setEditingAllocationId(null); setAllocType("fattura"); }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingAllocationId ? t("invoices.editAllocation") : "Alloca Movimento"}</DialogTitle>
            <DialogDescription>
              {allocDialog.movement && `${allocDialog.movement.descrizione} - ${formatCurrency(allocDialog.movement.importo)}`}
            </DialogDescription>
          </DialogHeader>
          
          {/* Residuo da allocare */}
          {allocDialog.movement && (
            <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
              <div className="text-sm">
                <span className="text-muted-foreground">Importo movimento:</span>
                <span className="font-medium ml-2">{formatCurrency(Math.abs(allocDialog.movement.importo))}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Già allocato:</span>
                <span className="font-medium ml-2">{formatCurrency(allocDialog.allocations.filter(a => a.id !== editingAllocationId).reduce((sum, a) => sum + (a.importo || 0), 0))}</span>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Residuo:</span>
                <span className={`font-bold ml-2 ${calcolaResiduo() > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {formatCurrency(calcolaResiduo())}
                </span>
              </div>
            </div>
          )}
          
          {allocDialog.allocations.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Allocazioni esistenti:</Label>
              <div className="border rounded-lg divide-y max-h-48 overflow-auto">
                {allocDialog.allocations.map((a) => (
                  <div key={a.id} className={`flex items-center justify-between p-2 text-sm ${a.is_incoming_giroconto ? 'bg-blue-50/50 dark:bg-blue-900/10' : editingAllocationId === a.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}>
                    <div className="flex-1">
                      {a.is_incoming_giroconto ? (
                        <>
                          <span className="font-medium">{formatCurrency(a.importo)}</span>
                          <span className="text-blue-600 ml-2">← Giroconto da: {a.source_banca_nome || 'Banca'} - {a.source_descrizione?.substring(0,40)}</span>
                          <span className="text-muted-foreground ml-1 text-xs">({new Date(a.source_data).toLocaleDateString('it-IT')})</span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium">{formatCurrency(a.importo)}</span>
                          {a.invoice_numero && <span className="text-muted-foreground ml-2">Fatt. {a.invoice_numero}</span>}
                          {a.account_codice && <span className="text-muted-foreground ml-2">Conto {a.account_codice} - {a.account_nome}</span>}
                          {a.cost_center_codice && <Badge variant="secondary" className="ml-2">{a.cost_center_codice}</Badge>}
                        </>
                      )}
                    </div>
                    {!a.is_incoming_giroconto && (
                    <div className="flex gap-1">
                      {editingAllocationId === a.id ? (
                        <Button variant="ghost" size="sm" onClick={cancelEditAllocation}>
                          <X className="h-3 w-3" />
                        </Button>
                      ) : (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => startEditAllocation(a)} title={t("common.edit")}>
                            <Pencil className="h-3 w-3 text-blue-500" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAllocation(a.id)} title={t("common.delete")}>
                            <Trash2 className="h-3 w-3 text-red-500" />
                          </Button>
                        </>
                      )}
                    </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo Allocazione</Label>
                <Select value={allocType} onValueChange={setAllocType}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fattura">{t('invoices.title')}</SelectItem>
                    <SelectItem value="conto">{t('movements.account')}</SelectItem>
                    <SelectItem value="assicurazione">Assicurazione Veicolo</SelectItem>
                    <SelectItem value="bollo_auto">{t('common.vehicleTax')}</SelectItem>
                    <SelectItem value="carburante">Carburante</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("common.amount")}</Label>
                <Input type="number" step="0.01" value={allocForm.importo} onChange={(e) => setAllocForm({ ...allocForm, importo: e.target.value })} />
              </div>
            </div>
            
            {allocType === "fattura" && (
              <div>
                <Label>{t('invoices.title')}</Label>
                <SearchableSelect
                  options={invoices
                    .map(i => {
                      const totale = Math.abs(parseFloat(i.totale) || 0);
                      const pagato = Math.abs(parseFloat(i.importo_pagato) || 0);
                      const residuo = Math.max(0, totale - pagato);
                      const suffix = residuo < totale ? ` — residuo ${formatCurrency(residuo)}` : '';
                      return { value: i.id, label: `${i.numero} - ${i.fornitore_cliente} (${formatCurrency(totale)})${suffix}` };
                    })}
                  value={allocForm.invoice_id}
                  onValueChange={handleInvoiceSelect}
                  placeholder={t("dialogs.selectInvoice")}
                />
              </div>
            )}
            
            {(allocType === "conto" || allocType === "assicurazione" || allocType === "bollo_auto" || allocType === "carburante") && (
              <div>
                <Label>{t('movements.account')}</Label>
                <SearchableSelect
                  options={accounts.map(a => ({ value: a.id, label: `${a.codice} - ${a.nome || a.descrizione || ''}` }))}
                  value={allocForm.account_id}
                  onValueChange={(v) => setAllocForm({ ...allocForm, account_id: v })}
                  placeholder={t("common.selectPlaceholder")}
                />
                {(allocType === "assicurazione" || allocType === "bollo_auto" || allocType === "carburante") && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Il CDC sarà determinato dal collegamento al {allocType === "assicurazione" ? "record assicurazione" : allocType === "bollo_auto" ? "bollo auto" : "rifornimento"} nella tab dedicata.
                  </p>
                )}
              </div>
            )}
            
            <div>
              <Label>{t('costCenters.title')}</Label>
              <SearchableSelect
                options={costCenters.map(c => ({ value: c.id, label: `${c.codice} - ${c.nome}` }))}
                value={allocForm.cost_center_id}
                onValueChange={(v) => setAllocForm({ ...allocForm, cost_center_id: v })}
                placeholder={t("common.selectPlaceholder")}
              />
            </div>
            
            <div>
              <Label>{t("common.notes")}</Label>
              <Input value={allocForm.note} onChange={(e) => setAllocForm({ ...allocForm, note: e.target.value })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setAllocDialog({ ...allocDialog, open: false }); setEditingAllocationId(null); }}>{t("common.close")}</Button>
            {editingAllocationId && (
              <Button variant="outline" onClick={cancelEditAllocation}>Annulla Modifica</Button>
            )}
            <Button onClick={handleAddAllocation}>
              {editingAllocationId ? t("common.save") : t("invoices.addAllocation")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione singola */}
      <Dialog open={!!deleteSingleDialog} onOpenChange={(v) => !v && setDeleteSingleDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Eliminare questo movimento carta? Questa azione è irreversibile.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteSingleDialog(null)}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteSingleDialog)} data-testid="confirm-delete-single-card">{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione selezionati */}
      <Dialog open={deleteMultipleDialog} onOpenChange={setDeleteMultipleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Stai per eliminare <strong>{selectedIds.size}</strong> moviment{selectedIds.size === 1 ? 'o' : 'i'} carta selezionat{selectedIds.size === 1 ? 'o' : 'i'}. Questa azione è irreversibile.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteMultipleDialog(false)} disabled={deletingBulk}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleBulkDelete} disabled={deletingBulk} data-testid="confirm-bulk-delete-card">
              {deletingBulk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : `Elimina ${selectedIds.size} movimenti`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione totale */}
      <Dialog open={deleteAllDialog} onOpenChange={setDeleteAllDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmTotalDeletion")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Stai per eliminare <strong>TUTTI i {movements.length}</strong> movimenti carta. Questa azione è irreversibile e non può essere annullata.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllDialog(false)} disabled={deletingBulk}>{t("common.cancel")}</Button>
            <Button variant="destructive" onClick={handleDeleteAll} disabled={deletingBulk} data-testid="confirm-delete-all-card">
              {deletingBulk ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('common.deleting')}</> : `Elimina tutti (${movements.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
